import React, { useState, useEffect, useCallback } from 'react';
import FilterPill from '../FilterPill/FilterPill';
import { SUPPORTED_LANGUAGES, DISPLAY_NAME_TO_CODE } from 'constants/languages';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  onChange?: (activeLanguages: string[]) => void;
  initialLanguages?: string[];
}

const defaultLanguages = SUPPORTED_LANGUAGES.map(l => l.displayName);

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  onChange,
  initialLanguages
}) => {
  const getInitialState = useCallback(() => {
    return defaultLanguages.reduce((acc, lang) => {
      // If initialLanguages is provided, only activate those languages
      // Otherwise, activate all languages (default behavior)
      let isActive = true;
      if (initialLanguages) {
        // Check if the language or its code is in the initial languages
        const langCode = DISPLAY_NAME_TO_CODE[lang];
        const langEntry = SUPPORTED_LANGUAGES.find(l => l.displayName === lang);
        isActive = initialLanguages.includes(lang) ||
                  (langCode ? initialLanguages.includes(langCode) : false) ||
                  (langCode ? initialLanguages.includes(langCode.toLowerCase()) : false) ||
                  (langEntry ? initialLanguages.includes(langEntry.backendCode) : false);
      }
      return { ...acc, [lang]: isActive };
    }, {} as Record<string, boolean>);
  }, [initialLanguages]);

  const [active, setActive] = useState<Record<string, boolean>>(getInitialState());
  const [isExpanded, setIsExpanded] = useState(false);

  // Update state when props change
  useEffect(() => {
    setActive(getInitialState());
  }, [getInitialState]);

  const toggle = (lang: string) => {
    const updated = { ...active, [lang]: !active[lang] };
    setActive(updated);
    onChange?.(Object.keys(updated).filter((k) => updated[k]));
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Get selected languages
  const selectedLanguages = defaultLanguages.filter(lang => active[lang]);

  // Determine which languages to display
  // If no languages selected, default to showing English
  const displayLanguages = isExpanded
    ? defaultLanguages
    : (selectedLanguages.length > 0 ? selectedLanguages : ['English']);

  // Count hidden languages
  const hiddenCount = defaultLanguages.length - displayLanguages.length;

  return (
    <div className="language-selector">
      <label>Language:</label>
      <div className="language-selector-buttons">
        {displayLanguages.map((lang) => (
          <FilterPill
            key={lang}
            label={lang}
            active={active[lang]}
            onToggle={() => toggle(lang)}
          />
        ))}
        {!isExpanded && hiddenCount > 0 && (
          <button
            className="language-more-toggle"
            onClick={toggleExpanded}
          >
            + {hiddenCount} more
          </button>
        )}
        {isExpanded && (
          <button
            className="language-more-toggle"
            onClick={toggleExpanded}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;
